import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import BattleRaceIcon from "./SVGIcons/BattleRaceIcon";
import BattleColourIcon from "./SVGIcons/BattleColourIcon";
import BattleKartFootSVGIcon from "./SVGIcons/BattleKartFootSVGIcon";
import BattleSnakeIcon from "./SVGIcons/BattleSnakeIcon";

interface BookingKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BookingKeyModal: React.FC<BookingKeyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-items-center tw-justify-center tw-bg-gray-900 tw-bg-opacity-50">
      <div className="tw-bg-[#393B41] tw-max-w-[700px] tw-w-full tw-mx-[20px] tw-rounded-[16px] tw-shadow-lg tw-overflow-hidden tw-relative">
        {/* Close Button */}
        <button className="tw-absolute tw-top-[15px] tw-right-[15px] tw-text-white tw-z-10 tw-bg-[#EA581A] tw-rounded-lg tw-p-[5px]" onClick={onClose}>
          <AiOutlineClose className="tw-w-[30px] tw-h-[30px]" />
        </button>

        <div className="tw-p-[30px] tw-pb-[20px]">
          <h2 className="tw-text-[20px] xl:tw-text-[28px] tw-font-bold tw-text-center tw-mb-[15px] add-grobold-font tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A] tw-bg-clip-text tw-text-transparent tw-w-fit tw-mx-auto">
            Booking key
          </h2>

          <div className="tw-mb-[20px] tw-flex tw-flex-col tw-items-center">
            <div className="tw-relative tw-flex tw-justify-center">
              <div className="tw-relative tw-rounded-[8px] tw-overflow-hidden">
                <div className="tw-bg-white tw-rounded-t-[8px] tw-px-[12px] tw-py-[2px] tw-flex tw-flex-col tw-items-center tw-gap-[6px] tw-w-auto tw-min-w-[110px] xl:tw-min-w-[200px]">
                  <div className="tw-text-black tw-text-[16px] xl:tw-text-[24px] tw-font-medium tw-leading-tight">7:30pm</div>
                  <div className="tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A] tw-p-[2px] tw-rounded-[6px]">
                    <div className="tw-bg-white tw-rounded-[4px] tw-p-[2px] tw-flex tw-gap-[2px]">
                      <div className="tw-relative tw-w-[40px] tw-h-[40px] tw-rounded-[3px] tw-overflow-hidden">
                        <BattleRaceIcon />
                      </div>

                      <div className="tw-relative tw-w-[40px] tw-h-[40px] tw-rounded-[3px] tw-overflow-hidden">
                        <BattleColourIcon />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Price and seats section - attached to bottom of white block */}
                <div className="tw-flex tw-items-center tw-overflow-hidden">
                  {/* Price section - left side with orange border - smaller */}
                  <div className="tw-w-[50%]">
                    <div className="tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A] tw-w-full tw-h-full">
                      <div className="tw-bg-[#4A90E2] tw-text-white tw-text-[14px] tw-text-center tw-leading-tight tw-h-fit tw-px-[8px] tw-py-[1px] tw-flex tw-items-center tw-justify-center">
                          <div className="tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A] tw-w-fit tw-p-[2px] tw-rounded-[11px]">
                            <span className="tw-bg-[#4A90E2] tw-text-white tw-text-[14px] tw-p-[3px] tw-rounded-[11px] tw-block">$</span>
                          </div>
                      </div>
                    </div>
                  </div>

                  {/* Seats section - right side connected - bigger */}
                  <div className="tw-bg-black tw-px-[12px] tw-py-[6px] tw-rounded-br-[8px] tw-w-[50%]">
                    <div className="tw-text-white tw-text-[14px] tw-text-center tw-leading-tight">4 left</div>
                  </div>
                </div>
              </div>

              {/* Game presets text - positioned right next to the games border */}
              <div className="tw-absolute tw-left-[calc(81%+8px)] sm:tw-left-[calc(46%+44px)] md:tw-left-[calc(50%+44px)] lg:tw-left-[calc(50%+44px)] xl:tw-left-[calc(50%+44px)] tw-top-[46px] sm:tw-top-[58px] md:tw-top-[28px] lg:tw-top-[44px] xl:tw-top-[54px] tw-flex tw-items-center tw-gap-[6px] sm:tw-gap-[10px] xl:tw-whitespace-nowrap tw-max-w-[130px] tw-w-full xl:tw-w-fit sm:tw-max-w-none">
                <div className="tw-w-[60px] sm:tw-w-[60px] lg:tw-w-[60px] tw-h-[2px] tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A]"></div>
                <span className="tw-text-white tw-text-[10px] sm:tw-text-[12px] lg:tw-text-[13px] xl:tw-text-[14px] tw-font-bold add-grobold-font">Game presets for time slot</span>
              </div>

              {/* Price per person text - positioned left of the price section */}
              <div className="tw-absolute tw-right-[calc(91%+2px)] sm:tw-right-[calc(90%+12px)] md:tw-right-[calc(76%+18px)] lg:tw-right-[calc(88%+16px)] xl:tw-right-[calc(92%+2px)] tw-top-[82px] sm:tw-top-[90px] md:tw-top-[88px] lg:tw-top-[90px] xl:tw-top-[94px] tw-flex tw-items-center tw-gap-[6px] sm:tw-gap-[8px] sm:tw-whitespace-nowrap tw-flex-row-reverse">
                <div className="tw-w-[30px] sm:tw-w-[40px] lg:tw-w-[50px] xl:tw-w-[60px] tw-h-[2px] tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A]"></div>
                <span className="tw-text-white tw-text-[10px] sm:tw-text-[12px] xl:tw-text-[14px] tw-font-bold add-grobold-font tw-leading-tight">Price per person</span>
              </div>
            </div>
          </div>

          {/* Game Mode Icons - grouped with orange border */}
          <div className="tw-flex tw-flex-col tw-items-center tw-mb-[20px] tw-mt-[50px]">
            <div className="tw-relative">
              <div className="tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A] tw-p-[3px] tw-rounded-[12px]">
                <div className="tw-bg-[#393B41] tw-rounded-[9px] tw-p-[4px] tw-flex tw-justify-center tw-gap-[4px]">
                  {/* Battle Race Game Mode */}
                  <div className="tw-relative tw-w-[50px] xl:tw-w-[80px] tw-h-[50px] xl:tw-h-[80px] tw-rounded-[8px] tw-overflow-hidden">
                    <BattleRaceIcon />
                  </div>

                  {/* Battle Snake Game Mode */}
                  <div className="tw-relative tw-w-[50px] xl:tw-w-[80px] tw-h-[50px] xl:tw-h-[80px] tw-rounded-[8px] tw-overflow-hidden">
                    <BattleSnakeIcon />
                  </div>

                  {/* Battle Color Game Mode */}
                  <div className="tw-relative tw-w-[50px] xl:tw-w-[80px] tw-h-[50px] xl:tw-h-[80px] tw-rounded-[8px] tw-overflow-hidden">
                    <BattleColourIcon />
                  </div>

                  {/* Battle Foot Game Mode */}
                  <div className="tw-relative tw-w-[50px] xl:tw-w-[80px] tw-h-[50px] xl:tw-h-[80px] tw-rounded-[8px] tw-overflow-hidden">
                    <BattleKartFootSVGIcon />
                  </div>
                </div>
              </div>

              {/* Gradient line pointing down from middle of border */}
              <div className="tw-absolute tw-left-1/2 tw-transform tw--translate-x-1/2 tw-top-full">
                <div className="tw-w-[2px] tw-h-[20px] tw-bg-gradient-to-b tw-from-[#FAC618] tw-to-[#EA581A]"></div>
              </div>
            </div>

            {/* Bottom text - two lines */}
            <div className="tw-mt-[20px]">
              <p className="tw-text-white tw-text-center tw-text-[12px] xl:tw-text-[14px] tw-leading-[1.4] add-grobold-font">
                Click these icons at the bottom of the booking page
                <br />
                to learn more about the game modes
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingKeyModal;
